import { useModeAwareQuery, useModeAwareMutation } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { useApiConfig } from "@/providers/ApiConfigProvider";
import { useQueryClient } from "@tanstack/react-query";
import { generateMockPromises } from "@/lib/mock-data/collections-mock-data";
import { graphqlQuery } from "@/lib/api/graphql";
import { computePaginationWithCount } from "@/lib/graphql-pagination";
import type { PromiseToPay, PromiseInvoice } from "@/types/collections";

export interface PromiseToPayListItemApi {
  promiseId: string;
  customerId: number;
  companyName: string | null;
  customerName: string;
  customerAccountNumber: string | null;
  promiseDate: string;
  promisedAmount: number;
  basePromiseAmount?: number;
  currencyCode: string | null;
  status: string;
  confirmationMethod: string;
  createdBy: string;
  assignedUserId: number;
  assignedUserName: string | null;
  createdAt: string;
  notes: string | null;
  daysOverdue: number | null;
  isAtRisk: boolean;
  brokenPromisesCount: number;
  contactEmail: string | null;
  contactPhone: string | null;
  contactName: string | null;
  caseNumber: string | null;
  invoiceCount: number | null;
  lastAction: LastActionApi | null;
  linkedInvoices: PromiseInvoiceApi[];
}

export interface LastActionApi {
  type: string;
  description: string;
  performedBy: string;
  date: string;
}

export interface PromiseInvoiceApi {
  promiseInvoiceId: string;
  invoiceId: string;
  headerReference: string | null;
  promisedAmount: number;
  currentStatus: string;
  balanceRemaining: number;
  transactionDate: string | null;
  dueDate: string | null;
  transactionType: string | null;
  originalAmount: number | null;
  outstandingAmount: number | null;
  currency: string | null;
  description: string | null;
  accountCode: string | null;
  netAmount: number | null;
  vatAmount: number | null;
  subLedger: string | null;
  orderNumber: string | null;
  createdBy: string | null;
  modifiedBy: string | null;
  onDispute: boolean | null;
  customerName: string | null;
}

export interface PromiseToPayDashboardApi {
  totalPromises: number;
  pendingPromises: number;
  fulfilledPromises: number;
  brokenPromises: number;
  partialPromises: number;
  totalPromisedAmount: number;
  totalFulfilledAmount: number;
  dueToday: number;
  overdue: number;
  atRisk: number;
  activeAmount: number;
  brokenAmount: number;
  dueThisWeekCount: number;
  dueThisWeekAmount: number;
}

export interface PaginationApi {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface GetPromisesToPayApiResponse {
  data: PromiseToPayListItemApi[];
  pagination: PaginationApi;
  dashboard: PromiseToPayDashboardApi;
}

export interface GetPromisesToPayParams extends Record<string, string | number | boolean | string[] | undefined> {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  statuses?: string[];
  assignedUserId?: number;
  dateFrom?: string;
  dateTo?: string;
  createdAtFrom?: string;
  createdAtTo?: string;
  currencyCodes?: string[];
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

function mapStatusFromApi(status: string): PromiseToPay["status"] {
  const statusMap: Record<string, PromiseToPay["status"]> = {
    Pending: "Active",
    Active: "Active",
    Fulfilled: "Paid",
    Broken: "Broken",
    Partial: "Partially Paid",
    "Partially Paid": "Partially Paid",
    Paid: "Paid",
    Closed: "Closed",
  };
  return statusMap[status] ?? "Active";
}

function mapConfirmationMethodFromApi(method: string): PromiseToPay["confirmationMethod"] {
  const methodMap: Record<string, PromiseToPay["confirmationMethod"]> = {
    Phone: "Phone",
    Email: "Email",
    Portal: "Portal",
  };
  return methodMap[method] ?? "Phone";
}

function normalizeTransactionType(apiType: string): string {
  const typeMap: Record<string, string> = {
    invoice: "invoice",
    "credit note": "credit_note",
    credit_note: "credit_note",
    receipt: "receipt",
    "debit adjustment": "debit_adjustment",
    debit_adjustment: "debit_adjustment",
    "credit adjustment": "credit_adjustment",
    credit_adjustment: "credit_adjustment",
  };
  return typeMap[apiType.toLowerCase()] ?? "invoice";
}

function mapInvoiceFromApi(invoice: PromiseInvoiceApi): PromiseInvoice {
  return {
    id: invoice.promiseInvoiceId,
    transactionId: invoice.invoiceId || null,
    headerReference: invoice.headerReference ?? "",
    invoiceDate: invoice.transactionDate ? new Date(invoice.transactionDate) : new Date(),
    originalAmount: invoice.originalAmount ?? invoice.promisedAmount,
    promisedAmount: invoice.promisedAmount,
    currentStatus: invoice.currentStatus === "Paid" ? "Paid" : invoice.currentStatus === "Partially Paid" ? "Partially Paid" : "Unpaid",
    balanceRemaining: invoice.balanceRemaining,
    transactionDate: invoice.transactionDate ? new Date(invoice.transactionDate) : null,
    dueDate: invoice.dueDate ? new Date(invoice.dueDate) : null,
    transactionType: invoice.transactionType ? normalizeTransactionType(invoice.transactionType) : null,
    outstandingAmount: invoice.outstandingAmount ?? null,
    currency: invoice.currency ?? null,
    description: invoice.description ?? null,
    accountCode: invoice.accountCode ?? null,
    netAmount: invoice.netAmount ?? null,
    vatAmount: invoice.vatAmount ?? null,
    subLedger: invoice.subLedger ?? null,
    orderNumber: invoice.orderNumber ?? null,
    createdBy: invoice.createdBy ?? null,
    modifiedBy: invoice.modifiedBy ?? null,
    onDispute: invoice.onDispute ?? null,
    customerName: invoice.customerName ?? null,
  };
}

function mapLastActionTypeFromApi(type: string): PromiseToPay["lastAction"] extends { type: infer T } | undefined ? T : never {
  const typeMap: Record<string, 'call' | 'email' | 'sms' | 'note' | 'payment' | 'status_change'> = {
    call: "call",
    email: "email",
    sms: "sms",
    note: "note",
    payment: "payment",
    status_change: "status_change",
  };
  return typeMap[type.toLowerCase()] ?? "note";
}

function mapLastActionFromApi(lastAction: LastActionApi | null): PromiseToPay["lastAction"] {
  if (!lastAction) {
    return undefined;
  }

  return {
    type: mapLastActionTypeFromApi(lastAction.type),
    description: lastAction.description,
    performedBy: lastAction.performedBy,
    date: new Date(lastAction.date),
  };
}

export function mapPromiseFromApi(item: PromiseToPayListItemApi): PromiseToPay {
  return {
    id: item.promiseId,
    customerId: String(item.customerId),
    companyName: item.companyName ?? undefined,
    customerName: item.customerName,
    customerAccountNumber: item.customerAccountNumber ?? "",
    customerEmail: item.contactEmail ?? "",
    customerPhone: item.contactPhone ?? "",
    contactName: item.contactName ?? undefined,
    caseId: "",
    caseNumber: item.caseNumber ?? "",
    promiseDate: new Date(item.promiseDate),
    promisedAmount: item.promisedAmount,
    basePromiseAmount: item.basePromiseAmount ?? undefined,
    currencyCode: item.currencyCode ?? undefined,
    status: mapStatusFromApi(item.status),
    confirmationMethod: mapConfirmationMethodFromApi(item.confirmationMethod),
    createdBy: item.createdBy,
    createdAt: new Date(item.createdAt),
    notes: item.notes ?? "",
    invoices: item.linkedInvoices.map(mapInvoiceFromApi),
    invoiceCount: item.invoiceCount ?? 0,
    daysOverdue: item.daysOverdue ?? undefined,
    isAtRisk: item.isAtRisk,
    brokenPromisesCount: item.brokenPromisesCount,
    assignedUserId: item.assignedUserId,
    assignedUserName: item.assignedUserName ?? '',
    lastAction: mapLastActionFromApi(item.lastAction),
  };
}

export interface PromisesToPayKpis {
  activePromises: { count: number; totalValue: number };
  brokenPromises: { count: number; totalValue: number };
  dueThisWeek: { count: number; totalValue: number };
}

function computeMockKpis(promises: PromiseToPay[]): PromisesToPayKpis {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const processed = promises.map((promise) => {
    const promiseDate = new Date(promise.promiseDate);
    promiseDate.setHours(0, 0, 0, 0);
    if (promiseDate < today && promise.status === "Active") {
      return { ...promise, status: "Broken" as const };
    }
    return promise;
  });

  const active = processed.filter((p) => p.status === "Active");
  const broken = processed.filter((p) => p.status === "Broken");
  const dueThisWeek = processed.filter((p) => {
    const daysUntilDue = Math.ceil((p.promiseDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilDue >= 0 && daysUntilDue <= 7 && p.status === "Active";
  });

  return {
    activePromises: { count: active.length, totalValue: active.reduce((sum, p) => sum + p.promisedAmount, 0) },
    brokenPromises: { count: broken.length, totalValue: broken.reduce((sum, p) => sum + p.promisedAmount, 0) },
    dueThisWeek: { count: dueThisWeek.length, totalValue: dueThisWeek.reduce((sum, p) => sum + p.promisedAmount, 0) },
  };
}

function computeApiKpis(dashboard: PromiseToPayDashboardApi): PromisesToPayKpis {
  return {
    activePromises: { count: dashboard.pendingPromises, totalValue: dashboard.activeAmount },
    brokenPromises: { count: dashboard.brokenPromises, totalValue: dashboard.brokenAmount },
    dueThisWeek: { count: dashboard.dueThisWeekCount, totalValue: dashboard.dueThisWeekAmount },
  };
}

interface KpiGraphQLNode {
  statusName: string | null;
  promisedAmount: number;
  basePromiseAmount: number;
  promiseDate: string;
}

interface KpiGraphQLResponse {
  promisesToPay: {
    promiseToPayList: GraphQLConnection<KpiGraphQLNode>;
  };
}

const PROMISE_KPI_QUERY = `
  query PromiseToPayKpis($first: Int, $after: String, $filter: PromiseToPayListFilterInput) {
    promisesToPay {
      promiseToPayList(first: $first, after: $after, filter: $filter) {
        edges {
          node {
            statusName
            promisedAmount
            basePromiseAmount
            promiseDate
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

function computeKpisFromNodes(nodes: KpiGraphQLNode[], useBaseAmount: boolean): PromisesToPayKpis {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const endOfWeek = new Date(today);
  endOfWeek.setDate(today.getDate() + 7);

  let activeCount = 0;
  let activeTotal = 0;
  let brokenCount = 0;
  let brokenTotal = 0;
  let dueThisWeekCount = 0;
  let dueThisWeekTotal = 0;

  for (const node of nodes) {
    const amount = useBaseAmount ? node.basePromiseAmount : node.promisedAmount;
    const status = node.statusName ?? "";
    if (status === "Active") {
      activeCount++;
      activeTotal += amount;
      const promiseDate = new Date(node.promiseDate);
      promiseDate.setHours(0, 0, 0, 0);
      if (promiseDate >= today && promiseDate <= endOfWeek) {
        dueThisWeekCount++;
        dueThisWeekTotal += amount;
      }
    } else if (status === "Broken") {
      brokenCount++;
      brokenTotal += amount;
    }
  }

  return {
    activePromises: { count: activeCount, totalValue: activeTotal },
    brokenPromises: { count: brokenCount, totalValue: brokenTotal },
    dueThisWeek: { count: dueThisWeekCount, totalValue: dueThisWeekTotal },
  };
}

export interface PromisesListResult {
  promises: PromiseToPay[];
  pagination: PaginationApi;
}

function computeMockPagination(promises: PromiseToPay[], page: number, pageSize: number): PaginationApi {
  const totalItems = promises.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  return {
    page,
    pageSize,
    totalItems,
    totalPages,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1,
  };
}

// --- GraphQL types for Promise To Pay list ---

interface PromiseToPayGraphQLNode {
  promiseId: number;
  customerId: number;
  companyName: string | null;
  customerName: string | null;
  customerAccountNumber: string | null;
  promiseDate: string;
  promisedAmount: number;
  currencyCode: string | null;
  statusCode: string | null;
  statusName: string | null;
  confirmationMethodCode: string | null;
  confirmationMethodName: string | null;
  createdByName: string | null;
  assignedUserId: number;
  assignedUserName: string | null;
  createdAt: string;
  notes: string | null;
  isAtRisk: boolean;
  brokenPromisesCount: number;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  caseNumber: string | null;
  daysOverdue: number | null;
  invoiceCount: number | null;
  totalInvoicePromisedAmount: number | null;
  totalInvoiceBalanceRemaining: number | null;
  basePromiseAmount: number;
  lastActionType: string | null;
  lastActionDescription: string | null;
  lastActionActorName: string | null;
  lastActionDate: string | null;
}

interface GraphQLConnection<T> {
  edges: Array<{ node: T }>;
  pageInfo: {
    startCursor: string | null;
    endCursor: string | null;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

interface PromiseToPayListGraphQLResponse {
  promisesToPay: {
    promiseToPayList: GraphQLConnection<PromiseToPayGraphQLNode>;
  };
}

interface PromiseToPayCountGraphQLResponse {
  promisesToPay: {
    promiseToPayCount: number;
  };
}

const PROMISE_TO_PAY_LIST_QUERY = `
  query PromiseToPayList($first: Int, $after: String, $sort: [PromiseToPayListSortSortByInput!], $filter: PromiseToPayListFilterInput) {
    promisesToPay {
      promiseToPayList(first: $first, after: $after, sort: $sort, filter: $filter) {
        edges {
          node {
            promiseId
            customerId
            companyName
            customerName
            customerAccountNumber
            promiseDate
            promisedAmount
            currencyCode
            statusCode
            statusName
            confirmationMethodCode
            confirmationMethodName
            createdByName
            assignedUserId
            assignedUserName
            createdAt
            notes
            isAtRisk
            brokenPromisesCount
            contactName
            contactEmail
            contactPhone
            caseNumber
            daysOverdue
            invoiceCount
            totalInvoicePromisedAmount
            totalInvoiceBalanceRemaining
            basePromiseAmount
            lastActionType
            lastActionDescription
            lastActionActorName
            lastActionDate
          }
        }
        pageInfo {
          startCursor
          endCursor
          hasNextPage
          hasPreviousPage
        }
      }
    }
  }
`;

function mapGraphQLNodeToListItem(node: PromiseToPayGraphQLNode): PromiseToPayListItemApi {
  return {
    promiseId: String(node.promiseId),
    customerId: node.customerId,
    companyName: node.companyName,
    customerName: node.customerName ?? "",
    customerAccountNumber: node.customerAccountNumber,
    promiseDate: node.promiseDate,
    promisedAmount: node.promisedAmount,
    basePromiseAmount: node.basePromiseAmount,
    currencyCode: node.currencyCode ?? null,
    status: node.statusName ?? node.statusCode ?? "Active",
    confirmationMethod: node.confirmationMethodName ?? node.confirmationMethodCode ?? "Phone",
    createdBy: node.createdByName ?? "",
    assignedUserId: node.assignedUserId,
    assignedUserName: node.assignedUserName,
    createdAt: node.createdAt,
    notes: node.notes,
    daysOverdue: node.daysOverdue,
    isAtRisk: node.isAtRisk,
    brokenPromisesCount: node.brokenPromisesCount,
    contactEmail: node.contactEmail,
    contactPhone: node.contactPhone,
    contactName: node.contactName,
    caseNumber: node.caseNumber,
    invoiceCount: node.invoiceCount ?? null,
    lastAction: node.lastActionType
      ? {
          type: node.lastActionType,
          description: node.lastActionDescription ?? "",
          performedBy: node.lastActionActorName ?? "",
          date: node.lastActionDate ?? "",
        }
      : null,
    linkedInvoices: [],
  };
}

function buildGraphQLSortVariable(sortBy?: string, sortOrder?: "asc" | "desc") {
  if (!sortBy) return undefined;
  return [{ field: sortBy, order: sortOrder === "desc" ? "Descending" : "Ascending" }];
}

function buildStatusFilter(statuses?: string[], status?: string): { eq: string } | { containsValues: string[] } | undefined {
  if (statuses && statuses.length > 0) {
    return statuses.length === 1 ? { eq: statuses[0] } : { containsValues: statuses };
  }
  if (status) {
    return { eq: status };
  }
  return undefined;
}

function buildDateRangeFilter(from?: string, to?: string): { dateOnly: Record<string, string> } | undefined {
  if (!from && !to) return undefined;
  const range: Record<string, string> = {};
  if (from) range.ge = from;
  if (to) range.le = to;
  return { dateOnly: range };
}

function buildDateTimeDateOnlyFilter(from?: string, to?: string): { dateOnly: Record<string, string> } | undefined {
  if (!from && !to) return undefined;
  const range: Record<string, string> = {};
  if (from) range.ge = `${from}T00:00:00`;
  if (to) range.le = `${to}T23:59:59`;
  return { dateOnly: range };
}

export function buildGraphQLFilterVariable(params: GetPromisesToPayParams) {
  const filter: Record<string, unknown> = {};

  if (params.search) {
    filter.summary = params.search;
  }

  const statusFilter = buildStatusFilter(params.statuses, params.status);
  if (statusFilter) {
    filter.statusName = statusFilter;
  }

  if (params.assignedUserId !== undefined) {
    filter.assignedUserId = { eq: params.assignedUserId };
  }

  const promiseDateFilter = buildDateRangeFilter(params.dateFrom, params.dateTo);
  if (promiseDateFilter) {
    filter.promiseDate = promiseDateFilter;
  }

  const createdAtFilter = buildDateTimeDateOnlyFilter(params.createdAtFrom, params.createdAtTo);
  if (createdAtFilter) {
    filter.createdAt = createdAtFilter;
  }

  if (params.currencyCodes && params.currencyCodes.length > 0) {
    filter.currencyCode = params.currencyCodes.length === 1
      ? { eq: params.currencyCodes[0] }
      : { containsValues: params.currencyCodes };
  }

  return Object.keys(filter).length > 0 ? filter : undefined;
}

const PROMISE_TO_PAY_COUNT_QUERY = `
  query PromiseToPayCount($filter: PromiseToPayListFilterInput) {
    promisesToPay {
      promiseToPayCount(filter: $filter)
    }
  }
`;

function pageToCursor(page: number): string {
  // The backend uses base64-encoded int32 for cursors (page number - 1 = 0-indexed)
  const pageIndex = page - 1;
  // Create a base64-encoded int32 matching the C# BitConverter.GetBytes(int) format
  const buffer = new ArrayBuffer(4);
  const view = new DataView(buffer);
  view.setInt32(0, pageIndex, true); // little-endian to match C# BitConverter
  const bytes = new Uint8Array(buffer);
  return btoa(String.fromCharCode(...bytes));
}

function usePromiseToPayCount(params?: GetPromisesToPayParams) {
  const apiConfig = useApiConfig();
  const filter = buildGraphQLFilterVariable(params ?? {});

  return useModeAwareQuery<number, Error>(
    ["promises-to-pay-count", filter, params?.sortBy, params?.sortOrder],
    async ({ signal }) => {
      const variables: Record<string, unknown> = {};
      if (filter) {
        variables.filter = filter;
      }

      const response = await graphqlQuery<PromiseToPayCountGraphQLResponse>(
        apiConfig,
        PROMISE_TO_PAY_COUNT_QUERY,
        variables,
        signal,
      );

      return response.promisesToPay.promiseToPayCount;
    },
    0,
    {
      staleTime: 30_000,
      refetchOnMount: "always",
    }
  );
}

export function usePromisesList(params?: GetPromisesToPayParams) {
  const apiConfig = useApiConfig();
  const mockPromises = generateMockPromises();
  const queryParams: GetPromisesToPayParams = { page: 1, pageSize: 25, sortBy: "PromiseDate", sortOrder: "asc", ...params };
  const mockPage = queryParams.page ?? 1;
  const mockPageSize = queryParams.pageSize ?? 25;
  const mockPagination = computeMockPagination(mockPromises, mockPage, mockPageSize);
  const mockPagedPromises = mockPromises.slice((mockPage - 1) * mockPageSize, mockPage * mockPageSize);
  const mockResult: PromisesListResult = { promises: mockPagedPromises, pagination: mockPagination };

  return useModeAwareQuery<PromisesListResult, Error>(
    ["promises-to-pay", queryParams],
    async ({ signal }) => {
      const page = queryParams.page ?? 1;
      const pageSize = queryParams.pageSize ?? 25;

      const variables: Record<string, unknown> = {
        first: pageSize,
        sort: buildGraphQLSortVariable(queryParams.sortBy, queryParams.sortOrder),
        filter: buildGraphQLFilterVariable(queryParams),
      };

      if (page > 1) {
        variables.after = pageToCursor(page);
      }

      const response = await graphqlQuery<PromiseToPayListGraphQLResponse>(
        apiConfig,
        PROMISE_TO_PAY_LIST_QUERY,
        variables,
        signal,
      );

      const connection = response.promisesToPay.promiseToPayList;
      const promises = connection.edges.map((x) => mapPromiseFromApi(mapGraphQLNodeToListItem(x.node)));

      const pagination: PaginationApi = {
        page,
        pageSize,
        totalItems: promises.length,
        totalPages: 1,
        hasNextPage: connection.pageInfo.hasNextPage,
        hasPreviousPage: connection.pageInfo.hasPreviousPage,
      };

      return { promises, pagination };
    },
    mockResult,
    {
      staleTime: 0,
      refetchOnMount: "always",
    }
  );
}

export function usePromisesDashboard() {
  const { apiGet } = useApi();
  const mockPromises = generateMockPromises();
  const mockKpis = computeMockKpis(mockPromises);

  return useModeAwareQuery<PromisesToPayKpis, Error>(
    ["promises-to-pay-dashboard"],
    async ({ signal }) => {
      const response = await apiGet<PromiseToPayDashboardApi>("/api/v1/promises/dashboard", undefined, signal);
      return computeApiKpis(response);
    },
    mockKpis,
    {
      staleTime: 0,
      refetchOnMount: "always",
    }
  );
}

function usePromisesToPayKpis(params?: GetPromisesToPayParams, useBaseAmount = false) {
  const apiConfig = useApiConfig();
  const filter = buildGraphQLFilterVariable(params ?? {});
  const mockPromises = generateMockPromises();
  const mockKpis = computeMockKpis(mockPromises);

  return useModeAwareQuery<PromisesToPayKpis, Error>(
    ["promises-to-pay-kpis", filter, useBaseAmount],
    async ({ signal }) => {
      let allNodes: KpiGraphQLNode[] = [];
      let hasMore = true;
      let after: string | null = null;

      while (hasMore) {
        const variables: Record<string, unknown> = { first: 500 };
        if (filter) variables.filter = filter;
        if (after) variables.after = after;

        const response = await graphqlQuery<KpiGraphQLResponse>(
          apiConfig,
          PROMISE_KPI_QUERY,
          variables,
          signal,
        );

        const connection = response.promisesToPay.promiseToPayList;
        allNodes = [...allNodes, ...connection.edges.map(x => x.node)];
        hasMore = connection.pageInfo.hasNextPage;
        after = connection.pageInfo.endCursor;

        if (allNodes.length >= 5000) break;
      }

      return computeKpisFromNodes(allNodes, useBaseAmount);
    },
    mockKpis,
    {
      staleTime: 30_000,
      refetchOnMount: "always",
    }
  );
}

export function usePromiseById(promiseId: string | undefined) {
  const { apiGet } = useApi();
  const mockPromises = generateMockPromises();
  const mockPromise = mockPromises.find(p => p.id === promiseId) ?? null;

  return useModeAwareQuery<PromiseToPay | null, Error>(
    ["promise-to-pay", promiseId],
    async ({ signal }) => {
      if (!promiseId) return null;
      const response = await apiGet<PromiseToPayListItemApi>(`/api/v1/promises/${promiseId}`, undefined, signal);
      return mapPromiseFromApi(response);
    },
    mockPromise,
    {
      enabled: !!promiseId,
      staleTime: 0,
      refetchOnMount: "always",
    }
  );
}

export interface UpdatePromiseInvoiceItem {
  transactionId: number;
  promisedAmount: number;
}

export interface UpdatePromiseToPayRequest {
  promiseId: string;
  promiseDate: string;
  promisedAmount: number;
  confirmationMethodId?: number;
  assignedUserId?: number;
  notes?: string;
  status?: string;
  reactivateCase?: boolean;
  linkedInvoices?: UpdatePromiseInvoiceItem[];
}

const confirmationMethodToId: Record<string, number> = {
  Phone: 1,
  Email: 2,
  Portal: 3,
};

export function mapConfirmationMethodToId(method: string): number {
  return confirmationMethodToId[method] ?? 1;
}

export function useUpdatePromiseToPay() {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<PromiseToPayListItemApi, Error, UpdatePromiseToPayRequest>(
    async (request) => {
      const { promiseId, ...body } = request;
      return apiPut<PromiseToPayListItemApi>(`/api/v1/promises/${promiseId}`, body);
    },
    (request) => {
      const mockPromises = generateMockPromises();
      const existing = mockPromises.find(p => p.id === request.promiseId);
      const mockResponse: PromiseToPayListItemApi = {
        promiseId: request.promiseId,
        customerId: existing ? parseInt(existing.customerId) : 0,
        companyName: null,
        customerName: existing?.customerName ?? "Mock Customer",
        customerAccountNumber: existing?.customerAccountNumber ?? null,
        promiseDate: request.promiseDate,
        promisedAmount: request.promisedAmount,
        currencyCode: existing?.currencyCode ?? null,
        status: existing?.status ?? "Active",
        confirmationMethod: existing?.confirmationMethod ?? "Phone",
        createdBy: existing?.createdBy ?? "Demo User",
        assignedUserId: request.assignedUserId ?? existing?.assignedUserId ?? 0,
        assignedUserName: existing?.assignedUserName ?? null,
        createdAt: existing?.createdAt.toISOString() ?? new Date().toISOString(),
        notes: request.notes ?? null,
        daysOverdue: existing?.daysOverdue ?? null,
        isAtRisk: existing?.isAtRisk ?? false,
        brokenPromisesCount: existing?.brokenPromisesCount ?? 0,
        contactEmail: existing?.customerEmail ?? null,
        contactPhone: existing?.customerPhone ?? null,
        contactName: existing?.contactName ?? null,
        caseNumber: existing?.caseNumber ?? null,
        invoiceCount: existing?.invoiceCount ?? 0,
        lastAction: null,
        linkedInvoices: [],
      };
      return mockResponse;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["promises-to-pay"] });
        queryClient.invalidateQueries({ queryKey: ["promise-to-pay"] });
        queryClient.invalidateQueries({ queryKey: ["promises-to-pay-dashboard"] });
        queryClient.invalidateQueries({ queryKey: ["promise-audit-logs"] });
      },
    }
  );
}

export interface BulkReassignRequest {
  promiseIds: number[];
  assignedUserId: number;
  notes?: string;
}

export interface BulkUpdateStatusRequest {
  promiseIds: number[];
  status: string;
  notes?: string;
}

interface BulkOperationResponse {
  updatedCount: number;
}

export function useBulkReassignPromises() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<BulkOperationResponse, Error, BulkReassignRequest>(
    async (request) => {
      return apiPost<BulkOperationResponse>("/api/v1/promises/bulk-reassign", request);
    },
    (request) => {
      return { updatedCount: request.promiseIds.length };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["promises-to-pay"] });
        queryClient.invalidateQueries({ queryKey: ["promise-to-pay"] });
        queryClient.invalidateQueries({ queryKey: ["promises-to-pay-dashboard"] });
        queryClient.invalidateQueries({ queryKey: ["promise-audit-logs"] });
      },
    }
  );
}

export function useBulkUpdatePromiseStatus() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<BulkOperationResponse, Error, BulkUpdateStatusRequest>(
    async (request) => {
      return apiPost<BulkOperationResponse>("/api/v1/promises/bulk-update-status", request);
    },
    (request) => {
      return { updatedCount: request.promiseIds.length };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["promises-to-pay"] });
        queryClient.invalidateQueries({ queryKey: ["promise-to-pay"] });
        queryClient.invalidateQueries({ queryKey: ["promises-to-pay-dashboard"] });
        queryClient.invalidateQueries({ queryKey: ["promise-audit-logs"] });
      },
    }
  );
}

const defaultPagination: PaginationApi = {
  page: 1,
  pageSize: 25,
  totalItems: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

const defaultKpis: PromisesToPayKpis = {
  activePromises: { count: 0, totalValue: 0 },
  brokenPromises: { count: 0, totalValue: 0 },
  dueThisWeek: { count: 0, totalValue: 0 },
};

export function usePromisesPageData(params?: GetPromisesToPayParams, useBaseAmount = false) {
  const promisesQuery = usePromisesList(params);
  const kpisQuery = usePromisesToPayKpis(params, useBaseAmount);
  const countQuery = usePromiseToPayCount(params);

  const basePagination = promisesQuery.data?.pagination ?? defaultPagination;
  const totalCount = countQuery.data ?? 0;
  const pagination = computePaginationWithCount(basePagination, totalCount, params?.pageSize);

  return {
    promises: promisesQuery.data?.promises ?? [],
    pagination,
    kpis: kpisQuery.data ?? defaultKpis,
    isLoading: promisesQuery.isLoading || kpisQuery.isLoading,
    error: promisesQuery.error || kpisQuery.error,
    refetch: () => {
      promisesQuery.refetch();
      kpisQuery.refetch();
      countQuery.refetch();
    },
  };
}
